import { useRef } from 'react';
import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { AlertIconSmall, Button, NakedButton, SpinningLoader, Text, XIconSmall } from '@frontend/design-system';
import { CARD_CAPTURE_RULES, MediaValidationRules, useMediaFieldUpload } from '../../media-upload';

interface CardCaptureSideInputProps {
  type: 'front' | 'back';
  fieldKey: string;
  required: boolean;
  hasError: boolean;
  onChange: (mediaId: string | null) => void;
}

const containerStyle = css({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
});

const previewWrapperStyle = css({
  display: 'flex',
  flexDirection: 'column',
  width: 150,
  alignItems: 'center',
});

const previewImageContainerStyle = css({
  position: 'relative',
  width: '100%',
  height: 100,
});

const previewImageStyle = css({
  border: `1px dashed ${theme.colors.neutral90}`,
  borderRadius: theme.borderRadius.medium,
  padding: theme.spacing(0.5),
  width: '100%',
  height: '100%',
  objectFit: 'cover',
});

const overlayStyle = css({
  position: 'absolute',
  inset: 0,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(0.5),
  padding: theme.spacing(1),
  borderRadius: theme.borderRadius.medium,
  backgroundColor: 'rgba(0, 0, 0, 0.55)',
  textAlign: 'center',
});

const removeButtonStyle = css({
  marginTop: theme.spacing(1),
  color: theme.colors.text.critical,
  fontSize: theme.font.size.medium,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
});

const uploadButtonWrapperStyle = css({
  position: 'relative',
});

const retryButtonStyle = css({
  fontSize: theme.font.size.small,
  color: theme.colors.white,
  textDecoration: 'underline',
  fontWeight: theme.font.weight.bold,
});

const inlineTextStyle = css({
  margin: 0,
  color: theme.colors.white,
  fontWeight: theme.font.weight.bold,
});

const overlayErrorIconStyle = css({
  color: theme.colors.status.warning,
  flexShrink: 0,
});

export const CardCaptureSideInput = ({ type, fieldKey, required, hasError, onChange }: CardCaptureSideInputProps) => {
  const { t } = useTranslation('digital-forms-experience');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const rules: MediaValidationRules = { ...CARD_CAPTURE_RULES, maxFiles: 1, required };

  const { attachments, addFiles, remove, retry } = useMediaFieldUpload({
    fieldKey,
    rules,
    onChange: (mediaIds) => onChange(mediaIds[0] ?? null),
  });

  const attachment = attachments[0];

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) {
      addFiles(e.target.files);
      e.target.value = '';
    }
  };

  return (
    <div css={containerStyle} id={fieldKey} aria-invalid={hasError} tabIndex={-1}>
      {attachment && attachment.previewUrl ? (
        <div css={previewWrapperStyle}>
          <div css={previewImageContainerStyle}>
            <img src={attachment.previewUrl} alt={t('{{type}} side of card', { type })} css={previewImageStyle} />

            {attachment.status === 'uploading' && (
              <div css={overlayStyle}>
                <SpinningLoader size='xs' />
                <Text size='small' color='light' css={inlineTextStyle}>
                  {t('Uploading...')}
                </Text>
              </div>
            )}

            {attachment.status === 'error' && (
              <div css={overlayStyle}>
                <AlertIconSmall css={overlayErrorIconStyle} />
                <Text size='small' color='light' css={inlineTextStyle}>
                  {attachment.message}
                </Text>
                <NakedButton onClick={() => retry(attachment.id)} css={retryButtonStyle}>
                  {t('Retry')}
                </NakedButton>
              </div>
            )}
          </div>

          <NakedButton onClick={() => remove(attachment.id)} css={removeButtonStyle}>
            <XIconSmall />
            {t('Remove')}
          </NakedButton>
        </div>
      ) : (
        <div css={uploadButtonWrapperStyle}>
          <Button variant='secondary' type='button' onClick={() => fileInputRef.current?.click()}>
            {t('Picture of {{type}}', { type })}
          </Button>
          <input ref={fileInputRef} type='file' accept='image/*' hidden onChange={handleFileChange} />
        </div>
      )}
    </div>
  );
};
